import {ContentState, convertToRaw} from 'draft-js';
import htmlToDraft from './htmlToDraftjs/library';
import {reportConversionFailure} from './reportConversionFailure';

/**
 * Raised when edited content cannot be turned back into storable DraftJS.
 *
 * A distinct type because the caller has to be able to tell this apart from any
 * other throw: this one means "the user's edit is in the editor and not in
 * anything we can save", which needs to reach the user, not just a log.
 */
export class ContentConversionError extends Error {
	constructor(readonly cause: unknown) {
		super(`Could not convert edited content to DraftJS: ${cause instanceof Error ? cause.message : String(cause)}`);
		this.name = 'ContentConversionError';
	}
}

/**
 * Turns the editor's html back into the DraftJS that gets stored.
 *
 * This runs on blur, so its failure mode is the expensive one: before this had any
 * error handling, a throw here meant the save callback was never reached and
 * `Util.CommitMutation` was never called — so the correlation-ID error modal that
 * lives inside CommitMutation never fired either, and the edit was discarded in
 * silence. Throwing a recognisable error is what lets the caller say so.
 *
 * `report` exists for the drift detector, which round-trips stored content the user
 * never saved. A throw there is diagnostic, not a failed save, and reporting it would
 * put a phantom event next to the real ones in Sentry.
 */
export const convertFromHtmlToDraftJs = (html: string | undefined, {report = true}: {report?: boolean} = {}): string => {
	if (!html) {
		return '';
	}

	try {
		const blocksFromHtml = htmlToDraft(html);
		if (!blocksFromHtml) {
			// htmlToDraft returns null when it cannot read a body out of the html. The
			// destructuring below then threw a bare TypeError with no context at all.
			throw new Error('htmlToDraft returned no blocks');
		}

		const {contentBlocks, entityMap} = blocksFromHtml;
		const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

		// Fix the mention type to be lowercase otherwise it will not work if switching back to DraftJS
		return JSON.stringify(convertToRaw(contentState)).replaceAll('"type":"MENTION"', '"type":"mention"');
	} catch (e) {
		if (report) {
			reportConversionFailure('html-to-draftjs', e, html);
		}
		throw new ContentConversionError(e);
	}
};
